import type { LinkedUserDto } from "@/generated/model/linkedUserDto";

/**
 * Ordering and search for the PIN-login name picker. Pure so it can be
 * unit-tested — the shell itself has no component-test harness.
 */

/** Unicode combining marks, i.e. what NFD splits an accent off into. */
const COMBINING_MARKS = /[̀-ͯ]/g;

/**
 * Sort the roster A–Z on the name actually shown on the card, so the rendered
 * order matches what someone scanning the list expects. `sensitivity: "base"`
 * makes it case- and accent-insensitive, so "amy" sorts before "Zoe" and
 * accented names collate next to their unaccented spelling.
 *
 * Locked and PIN-less users keep their alphabetical position rather than sinking
 * to the bottom: a user hunting for their own name shouldn't have to know their
 * account state to know where to look.
 */
export function sortUsersByName(users: LinkedUserDto[]): LinkedUserDto[] {
  return [...users].sort((a, b) =>
    a.displayName.localeCompare(b.displayName, undefined, {
      sensitivity: "base",
    }),
  );
}

/**
 * Lower-case and strip diacritics, so searching matches on the same terms the
 * sort collates on — otherwise "emi" would fail to find "Émile", which a tablet
 * keyboard makes easy to type and hard to diagnose.
 */
function fold(value: string): string {
  return value.normalize("NFD").replace(COMBINING_MARKS, "").toLowerCase();
}

/**
 * Initials for a user's avatar, taken from the first and last word of the name
 * shown on the card — the roster carries no separate name parts, since the
 * pre-auth payload is kept to what the picker actually draws.
 */
export function initialsFor(displayName: string): string {
  const words = displayName.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) {
    return "?";
  }
  const first = words[0]?.[0] ?? "";
  const last = words.length > 1 ? (words[words.length - 1]?.[0] ?? "") : "";
  return (first + last).toUpperCase();
}

/**
 * Filter the roster by a free-text query. Each whitespace-separated token must
 * appear somewhere in the name, in any order, so both "jo smi" and "smith jo"
 * find "Jo Smith". A blank query returns the list unchanged.
 */
export function filterUsersByQuery(
  users: LinkedUserDto[],
  query: string,
): LinkedUserDto[] {
  const tokens = fold(query).split(/\s+/).filter(Boolean);
  if (tokens.length === 0) {
    return users;
  }
  return users.filter((user) => {
    const name = fold(user.displayName);
    return tokens.every((token) => name.includes(token));
  });
}
